import { createContext } from 'react';
import { Run } from '../types/Run';
import { Session } from '../types/Session';
import { Config } from '../types/Config';

// The context object and its types live apart from the provider component so
// that consumers (hooks, indicators) can read the contract without importing
// the provider — which itself depends on those hooks.

export type ReprocessStatus = 'idle' | 'processing' | 'success' | 'error';
export type AutoCalculateStatus = 'idle' | 'processing' | 'success' | 'error';

/** Lifecycle phase of a single run that is being (or was just) reprocessed. */
export type ReprocessPhase = 'queued' | 'processing' | 'stalled' | 'error';

/** Per-run reprocess view model exposed to the UI by `getRunProgress`. */
export interface RunReprocessInfo {
  runId: string;
  phase: ReprocessPhase;
  stage: string | null; // raw processing_stage name — feed through humanizeStage() to display
  startedAt: string | null; // ISO processing_started_at, when known
}

export interface ReprocessOptions {
  /**
   * Rebuild each run's zip from its episode blob chunks (with the current
   * server reassembly code) before the pipeline runs, instead of trusting
   * the stored artifact. Persisted per run (rundata.reprocess_from_chunks)
   * — only meaningful for Nimbus blob-sourced runs (source_episode_id set).
   */
  fromChunks?: boolean;
}

export interface ReprocessContextType {
  sessionsToReprocess: Session[];
  configsToReprocess: Config[];
  runsToReprocess: Run[];
  shouldReprocess: boolean;
  reprocessStatus: ReprocessStatus;
  autoCalculateStatus: AutoCalculateStatus;
  isProcessing: boolean;
  isAutoCalculating: boolean;
  addSessionToReprocess: (session: Session) => void;
  addConfigToReprocess: (config: Config) => void;
  addRunToReprocess: (runs: Run[]) => void;
  removeSessionFromReprocess: (sessionId: string) => void;
  removeConfigFromReprocess: (configId: string) => void;
  removeRunFromReprocess: (runId: string) => void;
  removeAllReprocess: () => void;
  handleReprocess: (
    invalidateSession: () => void,
    immediateRuns?: Run[],
    options?: ReprocessOptions,
  ) => Promise<void>;
  handleAutoCalculate: (session: Session, invalidateSession: () => void) => Promise<void>;
  resetStatus: () => void;
  resetAutoCalculateStatus: () => void;
  /** Live per-run reprocess state, or undefined if the run is not being tracked. */
  getRunProgress: (runId: string) => RunReprocessInfo | undefined;
  /** True while a run is queued / processing (excludes stalled & error). */
  isRunReprocessing: (runId: string) => boolean;
  /**
   * Start polling /progress for runs that may already be in flight — both
   * reload recovery for an in-progress reprocess and freshly uploaded runs
   * working through their first pass.
   */
  watchRuns: (runIds: string[]) => void;
}

export const ReprocessContext = createContext<ReprocessContextType | null>(null);
